/** Value formatters shared by both apps. */

export const shortId = (v: string | null | undefined): string =>
  v ? String(v).slice(0, 8) : "";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = (v: unknown): boolean => UUID_RE.test(String(v ?? "").trim());

/**
 * The API sends naive ISO strings that are UTC (`datetime.utcnow().isoformat()`
 * / Postgres `now()` read back without a zone). `new Date("…T10:00:00")` with
 * no suffix is parsed as *local* time by the spec, which would shift every
 * timestamp by the viewer's offset — so the zone is appended before parsing.
 */
function parse(v: string | null | undefined): Date | null {
  if (!v) return null;
  const raw = String(v);
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(raw);
  const d = new Date(hasZone ? raw : raw + "Z");
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtDate(v: string | null | undefined): string {
  const d = parse(v);
  return d ? d.toLocaleString() : v ? String(v) : "";
}

const UNITS: [number, Intl.RelativeTimeFormatUnit][] = [
  [31557600, "year"],
  [2629800, "month"],
  [604800, "week"],
  [86400, "day"],
  [3600, "hour"],
  [60, "minute"],
];
const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto", style: "narrow" });

/** Relative time reads faster than a timestamp when scanning for "is this fresh?". */
export function ago(v: string | null | undefined): string {
  const d = parse(v);
  if (!d) return "";
  const secs = (Date.now() - d.getTime()) / 1000;
  const abs = Math.abs(secs);
  if (abs < 60) return "just now";
  for (const [size, unit] of UNITS) {
    if (abs >= size) return rtf.format(-Math.round(secs / size), unit);
  }
  return "just now";
}

export function fmtNum(v: unknown): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : n.toLocaleString();
}

export function fmtBytes(b: number | string | null | undefined): string {
  if (b == null || b === "") return "—";
  const n = Number(b);
  if (Number.isNaN(n)) return String(b);
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let i = -1;
  let x = n;
  do {
    x /= 1024;
    i++;
  } while (x >= 1024 && i < units.length - 1);
  return `${x.toFixed(x < 10 ? 1 : 0)} ${units[i]}`;
}

export function fmtDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

/** Pretty-print a string that may or may not hold JSON. */
export function pretty(s: unknown): string {
  if (s == null) return "";
  if (typeof s !== "string") return JSON.stringify(s, null, 2);
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

export const initial = (...candidates: (string | null | undefined)[]): string => {
  for (const c of candidates) {
    const t = (c ?? "").trim();
    if (t) return t[0]!.toUpperCase();
  }
  return "?";
};

export const plural = (n: number, one: string, many = one + "s"): string =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** Build a query string, dropping empty and nullish values. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === "" || v == null) continue;
    sp.set(k, String(v));
  }
  return sp.toString();
}
